import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { gardenAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Flower2,
  Leaf,
  TreeDeciduous,
  CheckCircle,
  Loader2
} from 'lucide-react';

export function GardenQuestionnaire({ open, onClose, onComplete }) {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      loadQuestionnaire();
    }
  }, [open]);

  const loadQuestionnaire = async () => {
    setLoading(true);
    try {
      const response = await gardenAPI.getQuestionnaire();
      setQuestions(response.data.questions || []);
      setCurrentIndex(0);
      setAnswers({});
      setResult(null);
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      toast.error('Erro ao carregar questionário');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (optionIndex) => {
    const question = questions[currentIndex];
    setAnswers(prev => ({
      ...prev,
      [question.id]: optionIndex
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Verificar se todas as perguntas foram respondidas
    const unanswered = questions.filter(q => answers[q.id] === undefined);
    if (unanswered.length > 0) {
      toast.error(`Por favor, responda todas as perguntas (${unanswered.length} faltando)`);
      // Ir para primeira pergunta não respondida
      const firstUnanswered = questions.findIndex(q => answers[q.id] === undefined);
      setCurrentIndex(firstUnanswered);
      return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([questionId, optionIndex]) => ({
        question_id: parseInt(questionId),
        option_index: optionIndex
      }));

      const response = await gardenAPI.submitQuestionnaire(formattedAnswers);
      setResult(response.data);
      toast.success(response.data.message || 'Questionário completado!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao enviar questionário';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = () => {
    onComplete(result);
  };

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;
  const isCurrentAnswered = currentQuestion && answers[currentQuestion.id] !== undefined;

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader className="sr-only"><DialogTitle>Carregando</DialogTitle></DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Result Screen
  if (result) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg" data-testid="questionnaire-result">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-6 w-6" />
              Seu Perfil Botânico
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            {/* Profile Result */}
            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl">
              <div className="text-6xl mb-4">
                {result.profile_type === 'intensity' && '🌻'}
                {result.profile_type === 'transition' && '🌾'}
                {result.profile_type === 'resilience' && '🌳'}
              </div>
              <h3 className="text-xl font-bold text-green-800 mb-2">
                {result.profile_name}
              </h3>
              <p className="text-sm text-green-700">
                {result.profile_type === 'intensity' && 
                  'Você está em um momento de florescimento rápido! Plantas anuais vão te dar feedback constante.'}
                {result.profile_type === 'transition' && 
                  'Você está construindo estrutura. Plantas bienais representam sua jornada de consolidação.'}
                {result.profile_type === 'resilience' && 
                  'Você tem raízes profundas. Plantas perenes simbolizam sua força e longevidade.'}
              </p>
            </div>

            {/* Scores */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground">Suas Pontuações:</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{result.scores.intensity}</p>
                  <p className="text-xs text-amber-700">Intensidade</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{result.scores.transition}</p>
                  <p className="text-xs text-blue-700">Transição</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">{result.scores.resilience}</p>
                  <p className="text-xs text-emerald-700">Resiliência</p>
                </div>
              </div>
            </div>

            {/* Recommended Plants */}
            {result.recommended_plants && result.recommended_plants.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-3">Plantas Recomendadas:</h4>
                <div className="grid grid-cols-3 gap-2">
                  {result.recommended_plants.map(plant => (
                    <div key={plant.id} className="text-center p-2 bg-white rounded-lg border">
                      <div className="text-2xl mb-1">
                        {plant.category === 'annual' && '🌻'}
                        {plant.category === 'biennial' && '🌾'}
                        {plant.category === 'perennial' && '🌳'}
                      </div>
                      <p className="text-xs font-medium truncate">{plant.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tokens Earned */}
            <div className="flex items-center justify-center gap-2 p-3 bg-yellow-50 rounded-lg">
              <Sparkles className="h-5 w-5 text-yellow-600" />
              <span className="font-medium text-yellow-700">
                +{result.tokens_earned} AWA ganhos!
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleComplete} className="w-full" data-testid="choose-plant-btn">
              Escolher Minha Planta
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Questionnaire Screen
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="questionnaire-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-green-600" />
            Anamnese Botânica
          </DialogTitle>
          <DialogDescription>
            Descubra qual planta representa sua jornada de recuperação
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Pergunta {currentIndex + 1} de {questions.length}</span>
            <span>{answeredCount} respondidas</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question */}
        {currentQuestion && (
          <div className="py-4 space-y-4">
            <h3 className="text-lg font-medium" data-testid="current-question">
              {currentQuestion.question}
            </h3>

            <div className="space-y-2">
              {currentQuestion.options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  className={`w-full p-4 text-left rounded-lg border transition-all ${
                    answers[currentQuestion.id] === idx
                      ? 'border-green-500 bg-green-50 ring-2 ring-green-500'
                      : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                  }`}
                  data-testid={`option-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      answers[currentQuestion.id] === idx
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-300'
                    }`}>
                      {answers[currentQuestion.id] === idx && (
                        <CheckCircle className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <span className="flex-1">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="flex-1 sm:flex-none"
              data-testid="prev-question-btn"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Anterior
            </Button>
            
            {currentIndex < questions.length - 1 ? (
              <Button
                onClick={handleNext}
                disabled={!isCurrentAnswered}
                className="flex-1 sm:flex-none"
                data-testid="next-question-btn"
              >
                Próxima
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={answeredCount < questions.length || submitting}
                className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
                data-testid="submit-questionnaire-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analisando...
                  </>
                ) : (
                  <>
                    Descobrir Perfil
                    <Sparkles className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
